/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useMemo, useState } from "react"
import { ConfigProvider, DatePicker, Select } from "@duxton-web/ui"
import dayjs, { Dayjs } from "dayjs"

import { CustomSelect } from "components/common"
import { SEARCH_LIMIT } from "components/search/searchBarConstants"
import { DEFAULT_DEBOUNCE_WAIT_TIME } from "constants/ui"
import {
  RETURN_WORKLIST_STATUS_GROUP_FILTER_OPTIONS,
  getReturnWorklistStatusGroupLabel,
} from "ecommerce/constants/returnRefund"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { MerchantStoreInfo } from "types/models/menu"
import { getDuxtonLocale, isTaiwanLocale } from "utils/i18n"
import { useLocaleContext, useTranslationsMap } from "common/utils/i18n"

import { optionValue } from "../consts"
import { FeedbackStoreFilter } from "../FeedbackFilters/hooks/useFeedbackStoreList"
import {
  formatDateForDisplay,
  getDefaultEndTimeBrowser,
  getDefaultFromTimeBrowser,
  onDeselectOption,
  onSelectOption,
} from "../Incidents/utils"
import styles from "./returnRefundStyles.modules.css"

const { RangePicker } = DatePicker

interface ReturnRefundListFilterProps {
  storeList: MerchantStoreInfo[] | undefined
  merchantID: string
  onChangeMerchantID(merchantID: string): void
  /** Browser-time `YYYY-MM-DD`, as held in the URL query params. */
  fromTime: string
  endTime: string
  onChangeDateRange(fromTime: string, endTime: string): void
  selectedStatus: string[]
  onChangeStatus(selected: string[]): void
  isSingleStoreUser: boolean
  handleFilter(filter: FeedbackStoreFilter): void
  isFetchingOutletsSearch: boolean
}

export const ReturnRefundListFilter: FC<ReturnRefundListFilterProps> = ({
  storeList,
  merchantID,
  onChangeMerchantID,
  fromTime,
  endTime,
  onChangeDateRange,
  selectedStatus,
  onChangeStatus,
  isSingleStoreUser,
  handleFilter,
  isFetchingOutletsSearch,
}) => {
  const locale = useLocaleContext("locale")
  const duxtonLocale = getDuxtonLocale(locale)
  const dateDisplayFormat = isTaiwanLocale(locale) ? "YYYY年M月D日" : undefined

  const [searchError, setSearchError] = useState("")

  const textMap = useTranslationsMapTyped({
    characterExceed: "common.characterExceed",
  })
  const text = useTranslationsMap({
    filterAll: "common.all",
    filterStatusActive: "common.active",
    filterStatusCancelled: "common.cancelled",
    filterStatusPlaceholder: "common.status",
    filterStatusResolved: "ecommerce.sellerCentre.returns.filterStatusResolved",
  })

  /**
   * Options must carry the backend's status-group values verbatim: the worklist validates
   * `statusFilter` against an allowlist and fails the whole request with invalid-argument
   * on anything else — which is what sending the FE placeholder taxonomy used to do.
   */
  const statusOptions = useMemo<{ label: string; value: string }[]>(() => {
    const groupLabel = getReturnWorklistStatusGroupLabel(text)

    return [
      { label: text.filterAll, value: optionValue.ALL },
      ...RETURN_WORKLIST_STATUS_GROUP_FILTER_OPTIONS.map(status => ({
        label: groupLabel[status],
        value: status as string,
      })),
    ]
  }, [text])

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    // Clearing the picker falls back to the same default window the tab opens with.
    if (!dates) {
      onChangeDateRange(getDefaultFromTimeBrowser(), getDefaultEndTimeBrowser())

      return
    }

    onChangeDateRange(
      dates[0]?.format(formatDateForDisplay) || "",
      dates[1]?.format(formatDateForDisplay) || "",
    )
  }

  const handleSearch = (value: string) => {
    if (value.length > SEARCH_LIMIT) {
      setSearchError(
        textMap.characterExceed
          .replace("<current>", `${value.length}`)
          .replace("<limit>", `${SEARCH_LIMIT}`),
      )

      return
    }

    setSearchError("")
    handleFilter({ search: value })
  }

  return (
    <div className={styles.filterContainer}>
      <ConfigProvider locale={duxtonLocale}>
        <RangePicker
          allowEmpty
          value={[
            fromTime ? dayjs(fromTime) : null,
            endTime ? dayjs(endTime) : null,
          ]}
          format={dateDisplayFormat}
          onChange={onRangeChange}
          minDate={dayjs().subtract(90, "days")}
          maxDate={dayjs().endOf("day")}
        />
      </ConfigProvider>
      <Select
        className={styles.fieldWidth}
        data-testid="input-status"
        options={statusOptions}
        placeholder={text.filterStatusPlaceholder}
        mode="multiple"
        maxTagCount={2}
        allowClear
        value={selectedStatus}
        onChange={onChangeStatus}
        onSelect={value =>
          onSelectOption(
            value as string,
            selectedStatus,
            onChangeStatus,
            statusOptions,
          )
        }
        onDeselect={value =>
          onDeselectOption(
            value as string,
            selectedStatus,
            onChangeStatus,
            statusOptions,
          )
        }
      />
      {!isSingleStoreUser && (
        <CustomSelect
          className={styles.fieldWidth}
          data-testid="input-store"
          options={storeList?.map(store => ({
            label: store.merchantName,
            value: store.merchantID,
          }))}
          searchable
          searchDelayMs={DEFAULT_DEBOUNCE_WAIT_TIME}
          loading={isFetchingOutletsSearch}
          onSearch={handleSearch}
          filterOption={false}
          customDropdownStyle={{ width: 400 }}
          onChange={onChangeMerchantID}
          paginationProps={{ showPagination: false }}
          value={merchantID}
          error={searchError}
        />
      )}
    </div>
  )
}
